/** @jsx jsx */

import React from "react";

import { jsx, SerializedStyles } from "@emotion/core";

import { Radio, Typography } from "antd";
import { Field, FieldProps, ErrorMessage } from "formik";
import { RadioChangeEvent } from "antd/lib/radio";

import { formHelperLabels as LABELS } from "./FormHelperLabels";
import { formHelperStyles } from "./FormHelperStyles";

const { Text } = Typography;

interface Props {
  name: string;

  /**
   * "keys" will be displayed on screen
   * "values" will be used for onChange
   */
  options: object;

  /**
   * Sets the default value of the Radio group
   */
  defaultValue?: string;

  label?: string;

  mandatory?: boolean;

  disabled?: boolean;
  hideError?: boolean;

  customError?: string;

  className?: string;

  /**
   * customStyles provided overrides default styles using
   * "Composition" behavior of emotion.
   * reference: https://emotion.sh/docs/composition
   */
  customStyles?: SerializedStyles;
  /**
   * Custom onchange function for a handling radio button
   * of type RadioChangeEvent
   */
  onChange?: (e: RadioChangeEvent) => void;
}

/**
 * a reusable, customizable Radio component to be used only within context of formik.
 */
const RadioButton: React.FC<Props> = (props: Props) => {
  const {
    name,
    label,
    defaultValue,
    className,
    mandatory,
    disabled,
    hideError,
    customError,
    customStyles,
    onChange,
    options
  } = props;
  const renderRadioForm = (
    <div className={className} css={[formHelperStyles, customStyles]}>
      {label && (
        <label className="d-block pb-2">
          {label}
          <Text className="mandatory">{mandatory && LABELS.mandatory}</Text>
        </label>
      )}
      <Field name={name}>
        {({ field }: FieldProps) => (
          <Radio.Group
            {...field}
            onChange={onChange || field.onChange}
            disabled={disabled}
            className="form-radio-btn pt-1"
            defaultValue={defaultValue}
          >
            {Object.entries(options).map(([key, value]) => (
              <Radio key={key} value={value}>
                {key}
              </Radio>
            ))}
          </Radio.Group>
        )}
      </Field>
      {!hideError && (
        <ErrorMessage name={name}>
          {msg => {
            const errorMessage = customError || msg; //To make sure only 1 error message is shown.
            return <Typography className="error">{errorMessage}</Typography>;
          }}
        </ErrorMessage>
      )}
    </div>
  );
  return renderRadioForm;
};

export default RadioButton;
